import { useState } from "react";
import { Drawer, Stack, Text, Button, Divider, UnstyledButton, Group, Box } from "@mantine/core";
import { IconMenu2, IconX } from "@tabler/icons-react";
import { Link, useLocation } from "react-router-dom";

const HEADER_NAVY = "#04305c";
const CTA_YELLOW = "#FFC107";
const CTA_TEXT_NAVY = "#002D62";
const HEADER_LINK = "#546E7A";

const pillRadius = { borderRadius: 9999 };

const ctaYellowStyles = {
  root: {
    ...pillRadius,
    backgroundColor: CTA_YELLOW,
    color: CTA_TEXT_NAVY,
    fontWeight: 700,
    fontSize: 14,
    border: "none",
    "&:hover": { backgroundColor: "#FFB300" },
  },
};

const loginFilledStyles = {
  root: {
    ...pillRadius,
    backgroundColor: HEADER_NAVY,
    color: "#fff",
    fontWeight: 700,
    fontSize: 14,
    border: "none",
    "&:hover": { backgroundColor: "#032746" },
  },
};

type MobileHamburgerProps = {
  isAuthenticated: boolean;
};

export default function MobileHamburger({ isAuthenticated }: MobileHamburgerProps) {
  const [opened, setOpened] = useState(false);
  const location = useLocation();

  const close = () => setOpened(false);

  const NavItem = ({ to, label, external }: { to: string; label: string; external?: boolean }) =>
    external ? (
      <UnstyledButton component="a" href={to} target="_blank" rel="noopener noreferrer" onClick={close} style={{ padding: "10px 12px", borderRadius: 8 }}>
        <Text fw={700} c={HEADER_LINK}>
          {label}
        </Text>
      </UnstyledButton>
    ) : (
      <UnstyledButton
        component={Link}
        to={to}
        onClick={close}
        style={{
          padding: "10px 12px",
          borderRadius: 8,
          background: location.pathname === to ? "#EEF4FF" : "transparent",
        }}
      >
        <Text fw={700} c={location.pathname === to ? "primary.6" : HEADER_LINK}>
          {label}
        </Text>
      </UnstyledButton>
    );

  return (
    <>
      {/*  Hamburger icon */}
      <UnstyledButton onClick={() => setOpened(true)} hiddenFrom="sm" aria-label="Open menu">
        <IconMenu2 size={26} color={HEADER_NAVY} stroke={2} />
      </UnstyledButton>

      {/*  Mobile Drawer */}
      <Drawer opened={opened} onClose={close} padding="lg" size="100%" position="left" withCloseButton={false}>
        {/* Header */}
        <Group justify="space-between" mb="md">
          <Text fw={700} size="lg" c="primary.6">
            GTsetu
          </Text>
          <UnstyledButton onClick={close}>
            <IconX size={22} />
          </UnstyledButton>
        </Group>

        <Stack gap="xs">
          {isAuthenticated ? (
            <>
              {/* Logged-in menu */}
              <NavItem to="/profile" label="My Profile" />
              <NavItem to="/" label="Discover" />
              <NavItem to="/dashboard" label="Dashboard" />
              <NavItem to="/collaborations" label="Collaborations" />
              <NavItem to="/shortlist" label="Shortlist" />
              <NavItem to="/invoices" label="Invoices" />
              <NavItem to="/support" label="Support" />
            </>
          ) : (
            <>
              {/* Public menu */}
              <Button component={Link} to="/" fullWidth mb="sm" onClick={close} styles={ctaYellowStyles}>
                Find Collaborations
              </Button>
              <NavItem to="https://gtsetu.com/what-we-do/" label="What We Do?" external />
              <NavItem to="https://gtsetu.com/features/" label="Features" external />
              <NavItem to="https://gtsetu.com/pricing/" label="Pricing" external />
              <NavItem to="https://gtsetu.com/support/" label="Support" external />
              <NavItem to="https://gtsetu.com/resources/" label="Resources" external />
              <NavItem to="https://gtsetu.com/blogs/" label="Blogs" external />
              <NavItem to="https://gtsetu.com/about/" label="About Us" external />
              <NavItem to="https://gtsetu.com/news/" label="News" external />

              <Divider my="md" />

              <Box>
                <Button component={Link} to="/login" fullWidth mb="sm" onClick={close} styles={loginFilledStyles}>
                  Log In
                </Button>
                <Button component={Link} to="/signup" fullWidth onClick={close} styles={ctaYellowStyles}>
                  Sign Up
                </Button>
              </Box>
            </>
          )}
        </Stack>
      </Drawer>
    </>
  );
}
